import { useState } from 'react';
import { useTodayEvent } from '../hooks/useEventData';
import { useAuth } from '../context/AuthContext';
import { useGroupData } from '../hooks/useGroupData';
import { formatDate, formatTime, getMapsUrl, formatPhone } from '../utils/helpers';
import { updateAttendance, claimDriving, releaseDriving } from '../api/client';
import EventBadge from '../components/EventBadge';
import LoadingSpinner from '../components/LoadingSpinner';
import type { AttendanceStatus, EventType } from '../types';

const gradients: Record<EventType, string> = {
  practice: 'from-sky-400 to-cyan-500 dark:from-sky-600 dark:to-cyan-700',
  regatta: 'from-purple-500 to-fuchsia-500 dark:from-purple-700 dark:to-fuchsia-700',
  competition: 'from-amber-400 to-orange-500 dark:from-amber-600 dark:to-orange-700',
};

export default function TodayViewC() {
  const { user, kids: myKids } = useAuth();
  const { event, nextEvent, attendance, driving, loading, error, refresh } = useTodayEvent();
  const { members, kids: allKids } = useGroupData();
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const handleAttendance = async (kidId: string, current: AttendanceStatus) => {
    if (!event) return;
    const next: AttendanceStatus = current === 'going' ? 'not_going' : 'going';
    setActionLoading(`att-${kidId}`);
    try {
      await updateAttendance(event.id, kidId, next);
      await refresh();
    } finally { setActionLoading(null); }
  };

  const handleDrive = async (type: 'pickup' | 'return') => {
    if (!event) return;
    setActionLoading(`drive-${type}`);
    try {
      await claimDriving(event.id, type);
      await refresh();
    } finally { setActionLoading(null); }
  };

  const handleReleaseDrive = async (type: 'pickup' | 'return') => {
    if (!event) return;
    setActionLoading(`release-${type}`);
    try {
      await releaseDriving(event.id, type);
      await refresh();
    } finally { setActionLoading(null); }
  };

  const getDriverName = (id: string | null) => id ? members.find(m => m.id === id)?.name || 'Unknown' : null;
  const getDriverPhone = (id: string | null) => id ? members.find(m => m.id === id)?.phoneNumber || null : null;
  const getKidAttendance = (kidId: string): AttendanceStatus => attendance.find(a => a.kidId === kidId)?.status || 'no_response';

  if (loading) return <LoadingSpinner className="mt-20" />;
  if (error) return <div className="p-6 text-center text-cancelled">{error}</div>;

  if (!event) {
    return (
      <div className="px-5 pt-8 pb-6 max-w-lg mx-auto">
        <h1 className="text-4xl font-black text-gray-900 dark:text-white leading-none">
          No Event<br />
          <span className="text-gray-300 dark:text-gray-700">Today</span>
        </h1>
        <p className="mt-3 text-lg text-gray-400">Take a breather. You've earned it.</p>

        {nextEvent && (
          <div className={`mt-8 rounded-3xl bg-gradient-to-br ${gradients[nextEvent.type]} p-5 text-white shadow-lg`}>
            <p className="text-xs font-bold uppercase tracking-widest opacity-80">Up Next</p>
            <p className="text-xl font-black mt-1">{formatDate(nextEvent.date)}</p>
            <p className="text-sm opacity-90 mt-1">
              {formatTime(nextEvent.startTime)} &bull; {nextEvent.locationName}
            </p>
          </div>
        )}
      </div>
    );
  }

  const pickupDriver = getDriverName(driving?.driverId || null);
  const pickupDriverPhone = getDriverPhone(driving?.driverId || null);
  const returnDriverId = driving?.returnDriverId || driving?.driverId || null;
  const returnDriver = getDriverName(returnDriverId);
  const isMyPickup = driving?.driverId === user?.id;
  const isMyReturn = driving?.returnDriverId === user?.id;
  const goingCount = allKids.filter(k => getKidAttendance(k.id) === 'going').length;

  return (
    <div className="px-5 pt-6 pb-6 max-w-lg mx-auto">
      {event.status === 'cancelled' ? (
        <>
          <h1 className="text-4xl font-black text-cancelled leading-none">Cancelled</h1>
          <p className="text-lg text-gray-400 mt-2">{formatDate(event.date)}</p>
          {event.notes && (
            <div className="mt-6 rounded-3xl bg-cancelled/5 dark:bg-cancelled/10 p-5">
              <p className="text-sm text-gray-600 dark:text-gray-400">{event.notes}</p>
            </div>
          )}
        </>
      ) : (
        <>
          {/* Gradient hero banner */}
          <div className={`rounded-3xl bg-gradient-to-br ${gradients[event.type]} p-6 text-white shadow-xl relative overflow-hidden`}>
            {/* Decorative circles */}
            <div className="absolute -top-8 -right-8 w-32 h-32 bg-white/10 rounded-full" />
            <div className="absolute -bottom-4 -left-4 w-20 h-20 bg-white/10 rounded-full" />

            <div className="relative z-10">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-black uppercase tracking-widest bg-white/20 px-2.5 py-1 rounded-full">
                  {event.type}
                </span>
              </div>
              {event.title && <p className="text-lg font-bold opacity-90 mb-1">{event.title}</p>}
              <h1 className="text-3xl font-black leading-tight">{formatDate(event.date)}</h1>

              <div className="mt-4 flex items-center gap-4">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider opacity-70">Time</p>
                  <p className="text-lg font-black">{formatTime(event.startTime)}-{formatTime(event.endTime)}</p>
                </div>
                {event.pickupTime && (
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider opacity-70">Pickup</p>
                    <p className="text-lg font-black">{formatTime(event.pickupTime)}</p>
                  </div>
                )}
              </div>

              <a
                href={getMapsUrl(event.locationName)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 mt-4 bg-white/20 hover:bg-white/30 backdrop-blur-sm px-4 py-2.5 rounded-2xl transition-colors min-h-tap"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
                </svg>
                <span className="font-bold text-sm">{event.locationName}</span>
              </a>
            </div>
          </div>

          {driving?.pickupSpot && (
            <div className="mt-3 px-4 py-2.5 rounded-2xl bg-gray-100 dark:bg-gray-800">
              <p className="text-xs font-bold text-gray-500 dark:text-gray-400">
                Pickup: <span className="text-gray-900 dark:text-white">{driving.pickupSpot}</span>
              </p>
            </div>
          )}

          {/* Driver pills */}
          <div className="mt-5 space-y-3">
            <p className="text-xs font-black uppercase tracking-widest text-gray-400 dark:text-gray-500 px-1">Drivers</p>

            {/* Pickup driver */}
            <div className="rounded-2xl bg-white dark:bg-gray-900 border-2 border-gray-100 dark:border-gray-800 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-1">To Event</p>
                  {pickupDriver ? (
                    <div>
                      <p className="text-lg font-black text-gray-900 dark:text-white">{pickupDriver}</p>
                      {pickupDriverPhone && <p className="text-sm text-gray-400">{formatPhone(pickupDriverPhone)}</p>}
                    </div>
                  ) : (
                    <p className="text-lg font-black text-warning">Needs a driver!</p>
                  )}
                </div>
                {pickupDriver ? (
                  isMyPickup ? (
                    <button onClick={() => handleReleaseDrive('pickup')} disabled={actionLoading === 'release-pickup'}
                      className="text-sm font-bold text-gray-400 hover:text-cancelled min-h-tap flex items-center px-3 transition-colors">
                      Drop
                    </button>
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                      <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                      </svg>
                    </div>
                  )
                ) : (
                  <button onClick={() => handleDrive('pickup')} disabled={actionLoading === 'drive-pickup'}
                    className={`bg-gradient-to-r ${gradients[event.type]} text-white font-black px-6 py-3 rounded-2xl min-h-tap transition-transform active:scale-95 disabled:opacity-50 shadow-lg`}>
                    {actionLoading === 'drive-pickup' ? '...' : "I'll Drive!"}
                  </button>
                )}
              </div>
            </div>

            {/* Return driver */}
            <div className="rounded-2xl bg-white dark:bg-gray-900 border-2 border-gray-100 dark:border-gray-800 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-1">Return</p>
                  {driving?.returnDriverId ? (
                    <p className="text-lg font-black text-gray-900 dark:text-white">{returnDriver}</p>
                  ) : pickupDriver ? (
                    <p className="text-sm text-gray-400">Same as pickup ({pickupDriver})</p>
                  ) : (
                    <p className="text-lg font-black text-warning">Needs a driver!</p>
                  )}
                </div>
                {driving?.returnDriverId ? (
                  isMyReturn && (
                    <button onClick={() => handleReleaseDrive('return')} disabled={actionLoading === 'release-return'}
                      className="text-sm font-bold text-gray-400 hover:text-cancelled min-h-tap flex items-center px-3 transition-colors">
                      Drop
                    </button>
                  )
                ) : (
                  <button onClick={() => handleDrive('return')} disabled={actionLoading === 'drive-return'}
                    className="border-2 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 font-bold px-5 py-2.5 rounded-2xl min-h-tap transition-all hover:border-practice hover:text-practice disabled:opacity-50 active:scale-95">
                    {actionLoading === 'drive-return' ? '...' : "I'll Drive Back!"}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Attendance */}
          <div className="mt-6">
            <div className="flex items-center gap-3 px-1 mb-3">
              <p className="text-xs font-black uppercase tracking-widest text-gray-400 dark:text-gray-500">
                Who's Going
              </p>
              <span className={`text-xs font-black px-2.5 py-0.5 rounded-full ${
                goingCount === allKids.length
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-500'
              }`}>
                {goingCount}/{allKids.length}
              </span>
            </div>

            <div className="grid gap-2">
              {allKids.map(kid => {
                const status = getKidAttendance(kid.id);
                const isMine = myKids.some(k => k.id === kid.id) || user?.role === 'admin';
                return (
                  <button
                    key={kid.id}
                    onClick={() => isMine && handleAttendance(kid.id, status)}
                    disabled={!isMine || actionLoading === `att-${kid.id}`}
                    className={`flex items-center justify-between px-4 py-3.5 rounded-2xl min-h-tap transition-all active:scale-[0.98] ${
                      status === 'going'
                        ? `bg-gradient-to-r ${gradients[event.type]} text-white shadow-md`
                        : status === 'not_going'
                        ? 'bg-gray-50 dark:bg-gray-900 border border-gray-100 dark:border-gray-800 opacity-50'
                        : 'bg-white dark:bg-gray-900 border-2 border-dashed border-warning/30 dark:border-warning/20'
                    } ${!isMine ? 'cursor-default' : ''}`}
                  >
                    <div className="text-left">
                      <p className={`font-bold ${status === 'going' ? 'text-white' : status === 'not_going' ? 'text-gray-400 dark:text-gray-500 line-through' : 'text-gray-900 dark:text-white'}`}>
                        {kid.name}
                      </p>
                      <p className={`text-xs ${status === 'going' ? 'text-white/70' : 'text-gray-400'}`}>
                        {members.find(m => m.id === kid.parentId)?.name}
                      </p>
                    </div>
                    <div className="flex items-center">
                      {status === 'going' && (
                        <span className="text-sm font-black bg-white/20 px-3 py-1 rounded-full">Going</span>
                      )}
                      {status === 'not_going' && (
                        <span className="text-xs font-bold text-gray-400">Out</span>
                      )}
                      {status === 'no_response' && (
                        <span className="text-xs font-bold text-warning animate-pulse">Tap to respond</span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Notes */}
          {event.notes && (
            <div className="mt-6 rounded-3xl bg-amber-50 dark:bg-amber-900/10 border border-amber-200/50 dark:border-amber-800/30 p-5">
              <p className="text-xs font-black uppercase tracking-widest text-amber-600/60 dark:text-amber-400/60 mb-1">Coach Notes</p>
              <p className="text-sm font-medium text-amber-900 dark:text-amber-200">{event.notes}</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
